<script setup>
import StatusChip from '@/components/shared/StatusChip.vue'
import {
  notificationPurposeLabel,
  useNotificationTemplatesStore,
} from '@/stores/notificationTemplates'

definePage({
  meta: {
    navActiveLink: 'notification-templates',
  },
})

const store = useNotificationTemplatesStore()
const router = useRouter()
const { t } = useI18n()

const createDialog = ref(false)
const formError = ref('')
const formSuccess = ref('')

const createForm = ref({
  purpose: 'coaching_update',
  name: '',
  subject: '',
  body: '',
})

const purposeOptions = [
  'appointment_reminder',
  'booking_confirmation',
  'schedule_update',
  'payment_reminder',
  'motivational_message',
  'coaching_update',
]

onMounted(() => {
  store.fetchNotificationTemplates().catch(() => null)
})

const applyFilters = () => {
  store.fetchNotificationTemplates().catch(() => null)
}

const openTemplate = id => {
  router.push({ name: 'notification-templates-id', params: { id } })
}

const openCreate = () => {
  formError.value = ''
  createForm.value = {
    purpose: 'coaching_update',
    name: '',
    subject: '',
    body: '',
  }
  createDialog.value = true
}

const submitCreate = async () => {
  formError.value = ''
  formSuccess.value = ''

  if (!createForm.value.name || !createForm.value.subject || !createForm.value.body) {
    formError.value = t('notificationTemplates.errors.requiredFields')

    return
  }

  try {
    const template = await store.createNotificationTemplate({ ...createForm.value })

    createDialog.value = false
    formSuccess.value = t('notificationTemplates.created')
    await router.push({ name: 'notification-templates-id', params: { id: template.id } })
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}
</script>

<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between gap-4 mb-6">
      <div>
        <h1 class="text-h4 mb-1">
          {{ $t('notificationTemplates.title') }}
        </h1>
        <p class="text-medium-emphasis mb-0">
          {{ $t('notificationTemplates.subtitle') }}
        </p>
      </div>
      <div class="d-flex flex-wrap gap-2">
        <VBtn
          variant="tonal"
          :to="{ name: 'notifications' }"
        >
          {{ $t('notificationTemplates.viewHistory') }}
        </VBtn>
        <VBtn
          color="primary"
          @click="openCreate"
        >
          {{ $t('notificationTemplates.create') }}
        </VBtn>
      </div>
    </div>

    <VAlert
      v-if="formSuccess"
      type="success"
      variant="tonal"
      class="mb-4"
      role="status"
    >
      {{ formSuccess }}
    </VAlert>

    <VAlert
      v-if="store.error"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ store.error }}
    </VAlert>

    <VCard
      class="mb-4"
      flat
      border
    >
      <VCardText class="d-flex flex-wrap gap-4">
        <VSelect
          v-model="store.filters.lifecycle_state"
          :items="[
            { title: $t('notificationTemplates.filters.allStatuses'), value: '' },
            { title: $t('notificationTemplates.status.draft'), value: 'draft' },
            { title: $t('notificationTemplates.status.active'), value: 'active' },
            { title: $t('notificationTemplates.status.archived'), value: 'archived' },
          ]"
          :label="$t('notificationTemplates.filters.status')"
          density="comfortable"
          style="max-inline-size: 220px; min-inline-size: 180px;"
          @update:model-value="applyFilters"
        />
        <VSelect
          v-model="store.filters.purpose"
          :items="[
            { title: $t('notificationTemplates.filters.allPurposes'), value: '' },
            ...purposeOptions.map(p => ({ title: notificationPurposeLabel(p), value: p })),
          ]"
          :label="$t('notificationTemplates.filters.purpose')"
          density="comfortable"
          style="max-inline-size: 280px; min-inline-size: 200px;"
          @update:model-value="applyFilters"
        />
      </VCardText>
    </VCard>

    <div
      v-if="store.loading"
      class="text-medium-emphasis"
      role="status"
    >
      {{ $t('notificationTemplates.loading') }}
    </div>

    <VAlert
      v-else-if="store.isEmpty"
      type="info"
      variant="tonal"
      role="status"
    >
      {{ $t('notificationTemplates.empty') }}
    </VAlert>

    <VList
      v-else
      class="bg-transparent"
    >
      <VListItem
        v-for="template in store.notificationTemplates"
        :key="template.id"
        class="px-0 mb-2"
        rounded
        border
        :aria-label="template.name"
        @click="openTemplate(template.id)"
      >
        <template #prepend>
          <VAvatar
            color="primary"
            variant="tonal"
            class="me-3"
          >
            <VIcon icon="ri-mail-line" />
          </VAvatar>
        </template>

        <VListItemTitle class="font-weight-medium">
          {{ template.name }}
        </VListItemTitle>
        <VListItemSubtitle>
          {{ notificationPurposeLabel(template.purpose) }} · {{ template.subject }}
        </VListItemSubtitle>

        <template #append>
          <StatusChip
            domain="notification_template"
            :state="template.lifecycle_state"
          />
          <VIcon
            icon="ri-arrow-right-s-line"
            class="ms-2"
          />
        </template>
      </VListItem>
    </VList>

    <VDialog
      v-model="createDialog"
      max-width="640"
    >
      <VCard>
        <VCardTitle>{{ $t('notificationTemplates.createDialogTitle') }}</VCardTitle>
        <VCardText>
          <VAlert
            v-if="formError"
            type="error"
            variant="tonal"
            class="mb-4"
            role="alert"
          >
            {{ formError }}
          </VAlert>

          <VSelect
            v-model="createForm.purpose"
            :items="purposeOptions.map(p => ({ title: notificationPurposeLabel(p), value: p }))"
            :label="$t('notificationTemplates.fields.purpose')"
            class="mb-3"
          />
          <VTextField
            v-model="createForm.name"
            :label="$t('notificationTemplates.fields.name')"
            class="mb-3"
          />
          <VTextField
            v-model="createForm.subject"
            :label="$t('notificationTemplates.fields.subject')"
            class="mb-3"
          />
          <VTextarea
            v-model="createForm.body"
            :label="$t('notificationTemplates.fields.body')"
            rows="5"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="createDialog = false"
          >
            {{ $t('common.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="store.saving"
            @click="submitCreate"
          >
            {{ $t('notificationTemplates.create') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
